import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw,
  Download,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '../auth/ParsleeAuthProvider';
import {
  adminApi,
  type PlatformSignupsPageResponse,
  type PlatformSignupsProjectionResponse,
  type PlatformSignupsPageParams,
  type PlatformSignupOrgRow,
  type PlatformOrphanAccountRow,
  type SignupSegment,
} from '../services/adminApi';
import { SignupsSummary } from './signups/SignupsSummary';
import { SignupsChart } from './signups/SignupsChart';
import { DateRangeBar, DATE_RANGE_LABELS } from './signups/DateRangeBar';
import {
  resolveRangeParams,
  isPresetRange,
  isCustomRangeInvalid,
  type DateRange,
  type SignupDateRow,
} from './signups/signupsAggregation';
import { resolveTimezone } from './signups/resolveTimezone';
import { buildCsv, csvFilename, downloadCsv } from './signups/signupsCsv';
import {
  ORG_COLUMNS,
  ORPHAN_COLUMNS,
  DEFAULT_SORT,
  isSortFieldValidForSegment,
  isLatestResponse,
  shouldAdoptProjection,
  isNewerAnchor,
  shouldReResolvePreset,
  pageWindow,
  countSegments,
  type SortField,
  type SortDir,
} from './signups/signupsView';
import { getOrgDetailUrl } from './adminNavigation';

const PAGE_SIZE = 50;

const SEGMENT_LABELS: Record<SignupSegment, string> = {
  real: 'Real orgs',
  demo: 'Demo & internal',
  orphan: 'Orphan accounts',
};

const EMPTY_COPY: Record<SignupSegment, string> = {
  real: 'No real customer signups match this view.',
  demo: 'No demo or internal orgs match this view.',
  orphan: 'No orphan accounts match this view.',
};

// ---------------------------------------------------------------------------
// Small formatters (kept local — mirror the aieweb signups page).
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffMs = Math.max(0, Date.now() - t);
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return sec === 0 ? 'just now' : `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function safeIso(iso: string | null): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toISOString() : 'unknown';
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

function formatNumber(n?: number | null): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function sortLabel(field: SortField, segment: SignupSegment): string {
  const cols = segment === 'orphan' ? ORPHAN_COLUMNS : ORG_COLUMNS;
  return cols.find((c) => c.field === field)?.label ?? field;
}

function isOrphanRow(
  row: PlatformSignupOrgRow | PlatformOrphanAccountRow
): row is PlatformOrphanAccountRow {
  return (row as PlatformOrphanAccountRow).accountId !== undefined;
}

export function getSignupOrgDetailUrl(
  row: PlatformSignupOrgRow | PlatformOrphanAccountRow
): string | null {
  if (isOrphanRow(row) || !row.orgId) return null;
  return getOrgDetailUrl(row.orgId, { from: 'signups' });
}

export function SignupTableRow({
  row,
  degradedStudio,
}: {
  row: PlatformSignupOrgRow | PlatformOrphanAccountRow;
  degradedStudio: boolean;
}) {
  if (isOrphanRow(row)) {
    return (
      <TableRow>
        <TableCell className="font-mono text-xs">{row.accountId}</TableCell>
        <TableCell>{row.name ?? '—'}</TableCell>
        <TableCell>{row.email}</TableCell>
        <TableCell>{formatDate(row.signupDate)}</TableCell>
      </TableRow>
    );
  }

  const orgDetailUrl = getSignupOrgDetailUrl(row);

  return (
    <TableRow>
      <TableCell className="text-foreground">
        {orgDetailUrl ? (
          <a className="signup-row-link" href={orgDetailUrl}>
            {row.orgName}
          </a>
        ) : (
          row.orgName
        )}
      </TableCell>
      <TableCell>{row.ownerName ?? '—'}</TableCell>
      <TableCell>{row.ownerEmail ?? '—'}</TableCell>
      <TableCell>{formatDate(row.signupDate)}</TableCell>
      <TableCell>{formatDate(row.lastActivityDate)}</TableCell>
      <TableCell className="text-right">{row.daysSinceLastActivity ?? '—'}</TableCell>
      <TableCell className="text-right">{formatNumber(row.tokensUsed)}</TableCell>
      <TableCell className="text-right">{formatNumber(row.coreChatCount)}</TableCell>
      <TableCell className="text-right">
        {degradedStudio ? '—' : formatNumber(row.studioProjectCount)}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SignupsTab() {
  const { user } = useAuth();
  const viewerEmail = user?.email ?? 'unknown';
  const timezone = useMemo(() => resolveTimezone(), []);

  // --- view state (survives refresh) ---
  const [segment, setSegment] = useState<SignupSegment>('real');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>(DEFAULT_SORT);
  const [skip, setSkip] = useState(0);
  const [dateRange, setDateRange] = useState<DateRange>({ preset: 'all' });
  const [customRangeError, setCustomRangeError] = useState<string | null>(null);

  // --- data ---
  const [page, setPage] = useState<PlatformSignupsPageResponse | null>(null);
  // The segment the current `page.rows` were fetched for — used to gate the
  // table render so a mid-switch never shows one segment's rows under another
  // segment's header (finding [3] desync class).
  const [pageSegment, setPageSegment] = useState<SignupSegment>('real');
  const [projection, setProjection] =
    useState<PlatformSignupsProjectionResponse | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const generatedAtRef = useRef<string | null>(null);
  useEffect(() => {
    generatedAtRef.current = generatedAt;
  }, [generatedAt]);
  // Snapshot moment the currently-adopted projection belongs to (finding [4]).
  const projectionGeneratedAtRef = useRef<string | null>(null);
  const projectionRetryRef = useRef(0);
  // The anchor the active preset window was last resolved against, so the
  // preset re-resolve fires at most once per newer snapshot (round-2 Bug B).
  const lastResolvedAnchorRef = useRef<string | null>(null);

  // Monotonic request sequence shared by ALL page-fetch writers (initial load,
  // scoping-key effect, preset re-resolve, Refresh) so a slow response never
  // clobbers a newer view (finding [2]).
  const seqRef = useRef(0);

  // --- status ---
  const [loading, setLoading] = useState(true); // first-load full-screen
  const [tableLoading, setTableLoading] = useState(false); // in-place refetch
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null); // first-load / 403
  const [inlineError, setInlineError] = useState<string | null>(null); // post-load

  const initializedRef = useRef(false);
  const lastPageKeyRef = useRef<string>('');

  // `now` anchors the KPI-card boundaries and the range chips to the snapshot's
  // data moment (falls back to the client clock when the snapshot is missing or
  // >5min stale).
  const now = useMemo(() => {
    const g = generatedAt ? new Date(generatedAt) : null;
    const gms = g?.getTime();
    const stale = !gms || !Number.isFinite(gms) || Date.now() - gms > 5 * 60 * 1000;
    return stale ? new Date() : g!;
  }, [generatedAt]);

  // Invalid custom range: both dates present and end < start. While invalid we
  // LOCK the view (finding [3+5]): no table-mutating handler commits, Refresh
  // and Download are disabled + early-return, and no request is issued — so the
  // table never silently reverts to all-time data and the header can't desync
  // from the rows. The date pickers stay live so the range can be corrected.
  const customInvalid = isCustomRangeInvalid(dateRange);

  useEffect(() => {
    setCustomRangeError(
      customInvalid ? 'End date must be on or after start date.' : null
    );
  }, [customInvalid]);

  const buildPageParams = useCallback(
    (overrides?: Partial<PlatformSignupsPageParams>): PlatformSignupsPageParams => {
      const { rangeStart, rangeEnd } = resolveRangeParams(dateRange, now, timezone);
      return {
        segment,
        search: debouncedSearch || undefined,
        sortField: sort.field,
        sortDir: sort.dir,
        skip,
        take: PAGE_SIZE,
        rangeStart,
        rangeEnd,
        ...overrides,
      };
    },
    [segment, debouncedSearch, sort, skip, dateRange, now, timezone]
  );

  // Composite key of everything that scopes the TABLE. Range INTENT (preset +
  // custom dates), not the resolved instants, so a generatedAt adopt is inert
  // here — preset re-resolution is handled by a dedicated effect below.
  const pageKey = useMemo(
    () =>
      JSON.stringify([
        segment,
        debouncedSearch,
        sort.field,
        sort.dir,
        skip,
        dateRange.preset,
        dateRange.customStart ?? '',
        dateRange.customEnd ?? '',
      ]),
    [segment, debouncedSearch, sort, skip, dateRange]
  );

  // --- projection sync (finding [4]) — refetch + adopt only when the fetched
  // projection's snapshot matches the page's currently-adopted snapshot; retry
  // is bounded and inlineError surfaces only on a persistent (>1) failure. ---
  const syncProjection = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const proj = await adminApi.getPlatformSignupsProjection();
        projectionRetryRef.current = 0; // a completed fetch is not a failure
        // Monotonic against the projection's OWN anchor (round-3): converge the
        // KPI/chart forward to the newest projection any replica can serve,
        // rather than futile-looping when the pinned page anchor's replica is
        // gone.
        if (shouldAdoptProjection(proj.generatedAt, projectionGeneratedAtRef.current)) {
          setProjection(proj);
          projectionGeneratedAtRef.current = proj.generatedAt;
          return;
        }
        // Fetched an OLDER projection than we already hold (a lagging replica) —
        // loop to try for a newer one (bounded; converges within a TTL).
      } catch {
        projectionRetryRef.current += 1;
        if (projectionRetryRef.current > 1) {
          setInlineError(
            'Could not refresh the summary and chart — they may be out of date.'
          );
        }
        return;
      }
    }
  }, []);

  // Adopt a page fetch's snapshot. The anchor only ever moves strictly FORWARD
  // (round-2 Bug B, monotonic) so alternating replicas can't ping-pong it or
  // make "Data as of" / the table jump backward. Advancing the anchor drives
  // the preset re-resolve effect (finding [1b]). Independently, keep the
  // projection on the CURRENT anchor (finding [4]).
  const adoptPageSnapshot = useCallback(
    (pageGeneratedAt: string) => {
      if (isNewerAnchor(pageGeneratedAt, generatedAtRef.current)) {
        generatedAtRef.current = pageGeneratedAt;
        setGeneratedAt(pageGeneratedAt);
      }
      // ACCEPTED RESIDUAL (round-3): the plan accepts per-replica snapshots (a
      // shared snapshot store is a deferred non-goal, outcomes line 166). Under
      // prod multi-replica skew the projection (KPI/chart) may briefly lag the
      // rendered table by up to one snapshot TTL (~120s) before converging. The
      // "Data as of" label tracks the rendered page's own snapshot, and the
      // projection converges monotonically — no reject-and-refetch beyond this.
      if (projectionGeneratedAtRef.current !== generatedAtRef.current) {
        void syncProjection();
      }
    },
    [syncProjection]
  );

  // --- imperative table (page-slice) fetch, seq-guarded (finding [2]). ---
  const fetchTable = useCallback(async () => {
    const mySeq = ++seqRef.current;
    const fetchedSegment = segment;
    // Record the anchor this fetch's (preset) window is resolved against so the
    // re-resolve effect stays idempotent per snapshot (round-2 Bug B).
    lastResolvedAnchorRef.current = generatedAtRef.current;
    setTableLoading(true);
    setInlineError(null);
    try {
      const res = await adminApi.getPlatformSignupsPage(buildPageParams());
      if (!isLatestResponse(mySeq, seqRef.current)) return;
      setPage(res);
      setPageSegment(fetchedSegment);
      adoptPageSnapshot(res.generatedAt);
    } catch (err) {
      if (isLatestResponse(mySeq, seqRef.current)) setInlineError(errorMessage(err));
    } finally {
      if (isLatestResponse(mySeq, seqRef.current)) setTableLoading(false);
    }
  }, [buildPageParams, adoptPageSnapshot, segment]);

  // Always call the LATEST fetchTable from effects without listing it as a dep
  // (which would re-fire on every state change).
  const fetchTableRef = useRef(fetchTable);
  fetchTableRef.current = fetchTable;

  // --- initial combined load (page + projection). Both must succeed or the
  // whole tab shows the error/retry state (never a half-rendered view).
  //
  // Deliberately mount-only: the deps are empty so this captures the initial
  // buildPageParams()/pageKey (segment=real, all-time, page 1). It is invoked
  // exactly twice — once on mount, and from the error-state Retry button, which
  // renders INSTEAD of the filter controls, so no segment/search/sort/range
  // change can occur before Retry. Adding buildPageParams/pageKey/syncProjection
  // to the deps would recreate this callback on every state change and, via the
  // `[runInitialLoad]` effect below, re-run the full-screen load on every
  // keystroke/paging — so the mount-only capture is intentional, not a latent
  // stale-closure bug (PR#11 finding). ---
  const runInitialLoad = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setInlineError(null);
    const mySeq = ++seqRef.current;
    try {
      const [pageRes, projRes] = await Promise.all([
        adminApi.getPlatformSignupsPage(buildPageParams()),
        adminApi.getPlatformSignupsProjection(),
      ]);
      if (!isLatestResponse(mySeq, seqRef.current)) return;
      setPage(pageRes);
      setPageSegment('real');
      setProjection(projRes);
      generatedAtRef.current = pageRes.generatedAt;
      setGeneratedAt(pageRes.generatedAt);
      projectionGeneratedAtRef.current = projRes.generatedAt;
      lastPageKeyRef.current = pageKey;
      initializedRef.current = true;
      // Initial page + projection can differ (replica skew) — reconcile so KPI
      // and table share one data moment.
      if (projRes.generatedAt !== pageRes.generatedAt) void syncProjection();
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      // Symmetry with fetchTable/handleRefresh: the latest writer clears the
      // shared table-loading state (round-2 Bug A).
      if (isLatestResponse(mySeq, seqRef.current)) setTableLoading(false);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    runInitialLoad();
  }, [runInitialLoad]);

  // --- table refetch when the scoping key changes (post-init) ---
  useEffect(() => {
    if (!initializedRef.current) return;
    if (pageKey === lastPageKeyRef.current) return;
    if (customInvalid) return; // hold the current view; show inline error
    lastPageKeyRef.current = pageKey;
    void fetchTableRef.current();
  }, [pageKey, customInvalid]);

  // --- preset re-resolve (finding [1b], hardened by round-2 Bug B): when a
  // genuinely newer snapshot anchor is adopted and a preset chip is active,
  // refetch ONCE so `rangeStart` (measured back from `now`) is re-derived
  // against the new anchor. The idempotency gate stops a refetch loop when
  // alternating replicas would otherwise keep flipping the anchor. ---
  useEffect(() => {
    if (!initializedRef.current) return;
    if (
      !shouldReResolvePreset(
        isPresetRange(dateRange),
        generatedAtRef.current,
        lastResolvedAnchorRef.current
      )
    ) {
      return;
    }
    void fetchTableRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedAt]);

  // --- Refresh: seq-guarded parallel page + projection fetch (refresh=true),
  // preserving the current view context. ---
  const handleRefresh = useCallback(async () => {
    if (refreshing || customInvalid) return;
    setRefreshing(true);
    setInlineError(null);
    const mySeq = ++seqRef.current;
    const fetchedSegment = segment;
    try {
      const [pageRes, projRes] = await Promise.all([
        adminApi.getPlatformSignupsPage(buildPageParams({ refresh: true })),
        adminApi.getPlatformSignupsProjection(true),
      ]);
      if (isLatestResponse(mySeq, seqRef.current)) {
        setPage(pageRes);
        setPageSegment(fetchedSegment);
        setProjection(projRes);
        projectionGeneratedAtRef.current = projRes.generatedAt;
        adoptPageSnapshot(pageRes.generatedAt);
      }
    } catch (err) {
      if (isLatestResponse(mySeq, seqRef.current)) setInlineError(errorMessage(err));
    } finally {
      // The superseding writer owns the shared loading state (round-2 Bug A):
      // clear it iff this refresh is still the latest, so an in-flight table
      // fetch it superseded can't leave the table stranded at aria-busy.
      if (isLatestResponse(mySeq, seqRef.current)) setTableLoading(false);
      setRefreshing(false);
    }
  }, [refreshing, customInvalid, buildPageParams, adoptPageSnapshot, segment]);

  // --- CSV: download the FULL filtered set (export=true), built client-side. ---
  const handleDownload = useCallback(async () => {
    if (downloading || customInvalid) return;
    setDownloading(true);
    setInlineError(null);
    try {
      const res = await adminApi.getPlatformSignupsPage(
        buildPageParams({ export: true })
      );
      const csv = buildCsv(segment, res.rows, viewerEmail, safeIso(res.generatedAt), {
        dateRangeLabel: DATE_RANGE_LABELS[dateRange.preset],
        textFilter: debouncedSearch || undefined,
        sortColumn: sortLabel(sort.field, segment),
        sortDirection: sort.dir,
      });
      downloadCsv(csv, csvFilename(segment));
    } catch (err) {
      setInlineError(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  }, [
    downloading,
    customInvalid,
    buildPageParams,
    segment,
    viewerEmail,
    dateRange.preset,
    debouncedSearch,
    sort,
  ]);

  // --- debounce search → commit + reset to page 1. Held while the custom range
  // is invalid so the view doesn't move; commits once the range is valid. ---
  useEffect(() => {
    if (customInvalid) return;
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSkip(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, customInvalid]);

  // --- handlers (locked while customInvalid, except the range itself) ---
  const handleSegmentChange = (next: SignupSegment) => {
    if (customInvalid) return;
    if (next === segment) return;
    setSegment(next);
    setSkip(0);
    // A sort column from the org table may not exist on the orphan table (and
    // vice versa) — reset to the default to avoid a 400 on an unknown field.
    setSort((prev) =>
      isSortFieldValidForSegment(prev.field, next) ? prev : DEFAULT_SORT
    );
  };

  const handleSort = (field: SortField) => {
    if (customInvalid) return;
    setSkip(0);
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: DEFAULT_SORT.dir }
    );
  };

  // Range changes must stay live even while invalid — this is the fix path.
  const handleRangeChange = (next: DateRange) => {
    setDateRange(next);
    setSkip(0);
  };

  const handlePrev = () => {
    if (customInvalid) return;
    setSkip((s) => Math.max(0, s - PAGE_SIZE));
  };
  const handleNext = () => {
    if (customInvalid) return;
    setSkip((s) => s + PAGE_SIZE);
  };

  // --- derived projection splits for KPI cards + chart ---
  const projectionRows = projection?.rows ?? [];
  const kpiOrgs: SignupDateRow[] = useMemo(
    () => projectionRows.filter((r) => r.segment !== 'orphan'),
    [projection]
  );
  const kpiOrphans: SignupDateRow[] = useMemo(
    () => projectionRows.filter((r) => r.segment === 'orphan'),
    [projection]
  );
  const chartReal: SignupDateRow[] = useMemo(
    () => projectionRows.filter((r) => r.segment === 'real'),
    [projection]
  );
  const chartOrphans = kpiOrphans;
  const segCounts = useMemo(() => countSegments(projectionRows), [projection]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // First-load: full-screen spinner or error/retry (or not-authorized).
  if (!page || !projection) {
    if (loadError) {
      return (
        <div className="signups-tab">
          <div className="admin-error">
            <AlertTriangle className="h-5 w-5" />
            <span>{loadError}</span>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Button variant="secondary" onClick={runInitialLoad}>
              Retry
            </Button>
          </div>
        </div>
      );
    }
    if (loading) {
      return (
        <div className="admin-loading">
          <Spinner size={32} label="Loading signups..." />
        </div>
      );
    }
    return null;
  }

  const degradedStudio = Boolean(page.degraded?.studio);
  const degradedPartial = Boolean(
    // Defensive: the wire key may arrive camelCased (`studioPartial`) or, if the
    // backend pins the legacy JsonPropertyName, snake-cased. Read both.
    page.degraded?.studioPartial ??
      (page.degraded as unknown as { studio_partial?: boolean })?.studio_partial
  );

  const win = pageWindow(page.skip, page.rows.length, page.totalCount);
  // LABEL HONESTY (round-3): drive "Data as of" from the snapshot the RENDERED
  // rows actually came from (page.generatedAt), not the max-ever monotonic
  // anchor — so the label can never claim newer than the rows it sits above.
  // The monotonic `generatedAt` anchor keeps its role gating preset re-resolve.
  const generatedAtAbs = safeIso(page.generatedAt);
  const generatedAtRel = formatRelative(page.generatedAt);

  const activeColumns = segment === 'orphan' ? ORPHAN_COLUMNS : ORG_COLUMNS;
  // Guard against rendering one segment's rows under another's header while a
  // segment switch is in flight (finding [3] desync class).
  const rowsMatchSegment = pageSegment === segment;

  return (
    <div className="signups-tab">
      {/* Header */}
      <div className="section-header">
        <div>
          <h2>Platform Signups</h2>
          <p
            className="text-sm text-muted-foreground"
            title={`Data as of: ${generatedAtAbs}`}
          >
            Data as of: {generatedAtRel}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing || customInvalid}
            aria-label="Refresh signups"
          >
            <RefreshCw className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={downloading || customInvalid}
            aria-label="Download CSV"
          >
            <Download />
            {downloading ? 'Preparing…' : 'Download CSV'}
          </Button>
        </div>
      </div>

      {/* KPI cards — lifetime, never scoped by search/range/segment */}
      <SignupsSummary orgs={kpiOrgs} orphans={kpiOrphans} now={now} timezone={timezone} />

      {/* Date-range chips — scope table + chart */}
      <DateRangeBar value={dateRange} onChange={handleRangeChange} error={customRangeError} />

      {/* Chart — real + orphan only (excludes demo & internal) */}
      <SignupsChart
        orgs={chartReal}
        orphans={chartOrphans}
        range={dateRange}
        now={now}
        timezone={timezone}
      />

      {/* Inline (post-load) error banner */}
      {inlineError && (
        <div className="admin-error" role="alert">
          <AlertTriangle className="h-5 w-5" />
          <span>{inlineError}</span>
        </div>
      )}

      {/* Degraded Studio banners */}
      {degradedStudio && (
        <div className="admin-error" role="alert" style={{ background: '#fef9c3', color: '#854d0e', borderColor: '#fde68a' }}>
          <AlertTriangle className="h-5 w-5" />
          <span>Studio unreachable — Studio project counts are unavailable.</span>
        </div>
      )}
      {degradedPartial && (
        <div className="admin-error" role="alert" style={{ background: '#fef9c3', color: '#854d0e', borderColor: '#fde68a' }}>
          <AlertTriangle className="h-5 w-5" />
          <span>Studio partial data — some organizations are missing project counts.</span>
        </div>
      )}

      {/* Table card */}
      <div className="subscriptions-section">
      {/* Segment tabs (whole-dataset counts) + search */}
      <div className="filters-row" role="tablist" aria-label="Signup segments">
        <div className="flex flex-wrap gap-2">
          {(['real', 'demo', 'orphan'] as SignupSegment[]).map((seg) => {
            const active = segment === seg;
            return (
              <button
                key={seg}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => handleSegmentChange(seg)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-text-secondary border border-border hover:bg-muted'
                }`}
              >
                {SEGMENT_LABELS[seg]} ({segCounts[seg]})
              </button>
            );
          })}
        </div>
        <div className="search-input relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by org name, owner name, or email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            aria-label="Search signups"
          />
        </div>
      </div>

      {/* Table (or empty / mid-switch state) */}
      <div style={{ overflowX: 'auto', opacity: tableLoading ? 0.6 : 1 }} aria-busy={tableLoading}>
        {!rowsMatchSegment ? (
          <div className="admin-loading" style={{ padding: '2rem' }}>
            <Spinner size={24} label="Loading…" />
          </div>
        ) : page.totalCount === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {EMPTY_COPY[segment]}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {activeColumns.map((col) => {
                  const active = sort.field === col.field;
                  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                  return (
                    <TableHead
                      key={col.field}
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className={col.align === 'right' ? 'text-right' : undefined}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(col.field)}
                        className={`inline-flex items-center gap-1 select-none hover:text-foreground ${
                          col.align === 'right' ? 'flex-row-reverse' : ''
                        }`}
                      >
                        {col.label}
                        <Icon className={`w-3 h-3 ${active ? 'text-foreground' : 'text-muted-foreground'}`} aria-hidden="true" />
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {page.rows.map((row) => {
                const rowKey = isOrphanRow(row) ? row.accountId : row.orgId;
                return (
                  <SignupTableRow
                    key={rowKey}
                    row={row}
                    degradedStudio={degradedStudio}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {rowsMatchSegment && page.totalCount > 0 && (
        <div className="pagination">
          <Button variant="secondary" onClick={handlePrev} disabled={win.isFirstPage || tableLoading}>
            Previous
          </Button>
          <span>
            Showing {win.rangeStart}–{win.rangeEnd} of {page.totalCount}
          </span>
          <Button variant="secondary" onClick={handleNext} disabled={win.isLastPage || tableLoading}>
            Next
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}

// Map an axios/thrown error to a user-facing message. 403 → not-authorized.
function errorMessage(err: unknown): string {
  const status =
    err && typeof err === 'object' && 'response' in err
      ? (err as { response?: { status?: number } }).response?.status
      : undefined;
  if (status === 403) {
    return 'You are not authorized to view signups. Platform signup access may be turned off.';
  }
  if (status === 429) {
    return 'Too many requests — please wait a moment and try again.';
  }
  return 'Failed to load signups. Please try again.';
}

export default SignupsTab;
